import { NavLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard,
  ScrollText,
  ShoppingCart,
  FileText,
  Users,
  UserCog,
  LogOut,
  Package,
  Package2,
  PackageSearch,
  Receipt,
  X,
  Archive,
  Globe,
  Activity,
  History,
  Truck,
  Radar,
  ServerCog,
  Calculator,
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { getUnmatchedEasyshipCount, getUnmatchedExportsCount, getUnassignedOrders } from '../lib/api'

// Client portal nav: My Account = data surface (orders, storage, …);
// Account Management = auth/team self-service (password, email, teammates).
const clientNav = [
  { to: '/account',          icon: LayoutDashboard, label: 'My Account'         },
  { to: '/rate-estimator',   icon: Calculator,      label: 'Rate Estimator'     },
  { to: '/account-settings', icon: UserCog,         label: 'Account Management' },
]

const adminNavBase = [
  { to: '/admin',                        icon: LayoutDashboard, label: 'Overview',            end: true },
  { to: '/admin/data-management',        icon: ServerCog,       label: 'Data Management'                },
  { to: '/admin/clients',                icon: Users,           label: 'Clients'                        },
  { to: '/admin/clients/archived',       icon: Archive,         label: 'Archived Clients'               },
  { to: '/admin/client-users',           icon: UserCog,         label: 'Client Admin'                   },
  { to: '/admin/activities',             icon: Activity,        label: 'Slack Activity'                 },
  { to: '/admin/billing',               icon: Receipt,         label: 'Billing'                        },
  { to: '/admin/history',               icon: History,         label: 'History'                        },
  { to: '/admin/mtd-log',               icon: ScrollText,      label: 'MTD Change Log'                 },
  { to: '/admin/tracking',               icon: Radar,           label: 'Tracking'                       },
  { to: '/admin/rate-estimator',         icon: Calculator,      label: 'Rate Estimator'                 },
  { to: '/admin/fedex',                  icon: Truck,           label: 'FedEx Reconciliation'           },
  { to: '/admin/auspost',                icon: Truck,           label: 'AusPost Reconciliation'         },
  { to: '/admin/ups',                    icon: Truck,           label: 'UPS Reconciliation'             },
  { to: '/admin/unmatched-exports',      icon: Globe,           label: 'Unmatched Exports', countKey: 'exports' },
  { to: '/admin/unmatched-easyship',     icon: Package2,        label: 'Unmatched Easyship', countKey: 'easyship' },
  { to: '/admin/unassigned-orders',      icon: PackageSearch,   label: 'Unassigned Orders', countKey: 'unassigned' },
]

function NavItem({ to, icon: Icon, label, end, badge, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        [
          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
          isActive
            ? 'bg-blue-600 text-white shadow-sm'
            : 'text-slate-300 hover:bg-slate-700 hover:text-white',
        ].join(' ')
      }
    >
      <Icon size={18} />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span className="text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none bg-orange-500 text-white">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </NavLink>
  )
}

export default function Sidebar({ onClose }) {
  const { role, user, logout } = useAuth()
  const navigate = useNavigate()

  // Fetch Easyship unmatched count for the badge (admin only)
  const { data: easyshipCountData } = useQuery({
    queryKey: ['easyship', 'unmatched', 'count'],
    queryFn: getUnmatchedEasyshipCount,
    enabled: role === 'admin',
    refetchInterval: 120_000,   // refresh every 2 minutes
    staleTime: 60_000,
  })
  const easyshipCount = easyshipCountData?.count ?? 0

  const { data: exportsCountData } = useQuery({
    queryKey: ['exports', 'unmatched', 'count'],
    queryFn: getUnmatchedExportsCount,
    enabled: role === 'admin',
    refetchInterval: 120_000,   // refresh every 2 minutes
    staleTime: 60_000,
  })
  const exportsCount = exportsCountData?.count ?? 0

  // No dedicated count endpoint — the queue is small by nature (hand-keyed
  // orders only), so the list itself is the count.
  const { data: unassignedRows } = useQuery({
    queryKey: ['unassigned-orders', 'count'],
    queryFn: () => getUnassignedOrders(false),
    enabled: role === 'admin',
    refetchInterval: 120_000,
    staleTime: 60_000,
  })
  const unassignedCount = (unassignedRows || []).length

  const badgeCounts = {
    easyship: easyshipCount, exports: exportsCount, unassigned: unassignedCount,
  }

  const nav = role === 'admin' ? adminNavBase : clientNav

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="h-full flex flex-col bg-slate-800 text-white">
      {/* Logo / branding */}
      <div className="flex items-center justify-between px-4 py-5 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center flex-shrink-0 p-1">
            <img src="/logo.png" alt="OPS 3PL" className="w-full h-full object-contain" />
          </div>
          <div>
            <p className="text-sm font-bold leading-tight tracking-tight">OPS 3PL</p>
            <p className="text-xs text-slate-400 leading-tight">Client Portal</p>
          </div>
        </div>
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="lg:hidden p-1 rounded text-slate-400 hover:text-white"
          aria-label="Close sidebar"
        >
          <X size={18} />
        </button>
      </div>

      {/* Nav section label */}
      <div className="px-4 pt-5 pb-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {role === 'admin' ? 'Administration' : 'Navigation'}
        </p>
      </div>

      {/* Nav links */}
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {nav.map((item) => (
          <NavItem
            key={item.to}
            {...item}
            badge={item.countKey ? badgeCounts[item.countKey] : 0}
            onClick={onClose}
          />
        ))}
      </nav>

      {/* User info + logout */}
      <div className="px-4 py-4 border-t border-slate-700 space-y-3">
        {user && (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-slate-200">
                {user.email?.[0]?.toUpperCase() ?? '?'}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-300 truncate">{user.email}</p>
              <span className={[
                'text-xs px-1.5 py-0.5 rounded font-medium',
                role === 'admin'
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-blue-500/20 text-blue-400',
              ].join(' ')}>
                {role}
              </span>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-red-600/20 hover:text-red-400 transition-colors"
        >
          <LogOut size={18} />
          Sign out
        </button>
      </div>
    </div>
  )
}
